import { Grid, Box, Stack, IconButton } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FaRegAddressCard } from 'react-icons/fa';
import { GrLocation, GrOrganization } from 'react-icons/gr';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { ILocation, IWorkspace, updateWorkspace } from 'wisestep-api';
import {
  Item,
  WsColors,
  WsIconText,
  WsTypography,
  WsTypoVariants,
  WsLocationSearch,
  WsButton,
  IWorkspaceContext,
} from 'wisestep-base';

import { success, failed } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import RecruiterProfileDetails from '../../RecruiterProfileDetails';
import RecruiterDetailsFields from '../RecruiterDetailsFields';
import { StyledTextareaAutosize } from '../userProfile/UserProfile';

interface WorkspaceProps {
  workspace: IWorkspace | IWorkspaceContext;
}

const WorkspaceInfo = ({ workspace }: WorkspaceProps) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [workspaceData, setWorkspaceData] = useState<IWorkspace | IWorkspaceContext>(workspace);

  const workspaceDetailsDefaultValues = {
    about: workspace?.about,
    allowJobAdminsToChangeSettings: workspace?.allowJobAdminsToChangeSettings,
    allowMultiLoginOfJobboards: workspace?.allowMultiLoginOfJobboards,
    city: workspace?.city,
    companyIdAtJobTarget: workspace?.companyIdAtJobTarget,
    createdBy: workspace?.createdBy,
    createdOn: workspace?.createdOn,
    enableAutoJoin: workspace?.enableAutoJoin,
    enableDNC: workspace?.enableDNC,
    id: workspace?.id ?? '',
    jobBoardSettings: workspace?.jobBoardSettings,
    link: workspace?.link,
    location: workspace?.location,
    maxUsers: workspace?.maxUsers,
    name: workspace?.name,
    onBoardingQuestionAnswered: workspace?.onBoardingQuestionAnswered,
    workspaceOrganizationName: workspace?.organization,
    ownerId: workspace?.ownerId,
    owns: workspace?.owns,
    planType: workspace?.planType,
    requiresMFA: workspace?.requiresMFA,
    roles: workspace?.roles,
    userId: workspace?.userId,
    address: workspace?.location?.area,
  };

  const formOptions = {
    defaultValues: workspaceDetailsDefaultValues,
  };
  const methods = useForm(formOptions);
  const { handleSubmit } = methods;

  useEffect(() => {
    if (workspace) setWorkspaceData(workspace);
  }, [workspace]);

  const onFormSubmit = async (data) => {
    const tempData = workspaceData as IWorkspace;
    if (tempData && data) {
      const addressInfo: ILocation = {
        city: data.location.city,
        continent: data.location.continent,
        continentCode: data.location.continentCode,
        country: data.location.country,
        countryCode: data.location.countryCode,
        state: data.location.state,
        stateCode: data.location.stateCode,
      };

      addressInfo.area = data.address;
      tempData.organization = data.workspaceOrganizationName;
      tempData.about = data.about;
      tempData.location = addressInfo;
      tempData.workspacePlan = null;

      try {
        await updateWorkspace(tempData as IWorkspaceContext);
        setNotification(success('Workspace details updated successfully!'));
      } catch (error) {
        setNotification(failed('Failed to save workspace details!'));
      }
    }
  };

  return (
    <RecruiterProfileDetails>
      <Item sx={{ backgroundColor: WsColors.White }}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onFormSubmit)}>
            <Box>
              <Grid container pb={2}>
                <Grid item xs={6} pb={2} justifyContent="flex-start">
                  <WsTypography variant={WsTypoVariants.TITLE}>
                    Workspace related details
                  </WsTypography>
                </Grid>
                {workspace.owns && (
                  <Grid item container xs={6} justifyContent="flex-end">
                    <WsButton size="large" type="submit">
                      Save
                    </WsButton>
                  </Grid>
                )}
              </Grid>
              <Grid container spacing={2} pt={1}>
                <Grid item xs={6}>
                  <RecruiterDetailsFields
                    name="address"
                    iconText={
                      <WsIconText
                        icon={<FaRegAddressCard size={20} />}
                        text="Address*"
                        textSize={WsTypoVariants.BODY1}
                      />
                    }
                    type="text"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Stack direction="column">
                    <Grid pl={1} pt={1}>
                      <WsIconText
                        icon={<GrLocation size={15} />}
                        text="Location*"
                        textSize={WsTypoVariants.BODY1}
                      />
                    </Grid>
                    <WsLocationSearch
                      name="location"
                      placeHolder=""
                      size="medium"
                      multiSelect={false}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <RecruiterDetailsFields
                    name="workspaceOrganizationName"
                    iconText={
                      <WsIconText
                        icon={<GrOrganization size={15} />}
                        text="Name of Your Organization*"
                        textSize={WsTypoVariants.BODY1}
                      />
                    }
                    type="text"
                  />
                </Grid>
                <Grid item xs={8}>
                  <IconButton>
                    <WsIconText
                      icon={<IoMdInformationCircleOutline size={20} />}
                      text="About"
                      textSize={WsTypoVariants.BODY1}
                    />
                  </IconButton>
                  <StyledTextareaAutosize
                    minRows={10}
                    placeholder=""
                    name="about"
                    defaultValue={workspace.about}
                  />
                </Grid>
              </Grid>
            </Box>
          </form>
        </FormProvider>
      </Item>
    </RecruiterProfileDetails>
  );
};

export default WorkspaceInfo;
